import {useEffect, useRef, useState} from 'react';
import {
    useDispatch as useReactReduxDispatch,
    useSelector as useReactReduxSelector,
} from 'react-redux';
import {isEqual} from 'lodash';

import type {RootState, AppDispatch} from '../../store';

export const useDispatch = useReactReduxDispatch.withTypes<AppDispatch>();
export const useSelector = useReactReduxSelector.withTypes<RootState>();

export const useAuthorized = (
    requiredRole?: 'editor' | 'translator' | 'impersonator'
) => {
    const {user: storedUser, token} = useSelector(state => state.auth);
    const isAuthenticated = !!storedUser && !!token;
    const user = isAuthenticated ? storedUser : null;

    const roleMap = {
        editor: user?.isEditor,
        translator: user?.isTranslator,
        impersonator: user?.isImpersonator,
    };

    const isAuthorized = requiredRole
        ? !!roleMap[requiredRole]
        : isAuthenticated;

    return {isAuthorized, isAuthenticated, user};
};

export const useAuthenticated = () => {
    const {user: storedUser, token} = useSelector(state => state.auth);
    const isAuthenticated = !!storedUser && !!token;
    const user = isAuthenticated ? storedUser : null;
    const isEditor = !!user?.isEditor;
    const isAdmin = !!user?.isAdmin;

    return {isAuthenticated, user, isEditor, isAdmin, token};
};

export const useDeepCompareEffect = (
    callback: React.EffectCallback,
    dependencies: any[]
) => {
    const currentDependenciesRef = useRef<any[]>([]);

    if (!isEqual(currentDependenciesRef.current, dependencies)) {
        currentDependenciesRef.current = dependencies;
    }

    useEffect(callback, [currentDependenciesRef.current]);
};

export const useNetworkStatus = () => {
    const [isOnline, setOnline] = useState<boolean>(navigator.onLine);

    const updateNetworkStatus = () => {
        setOnline(navigator.onLine);
    };

    useEffect(() => {
        window.addEventListener('load', updateNetworkStatus);
        window.addEventListener('online', updateNetworkStatus);
        window.addEventListener('offline', updateNetworkStatus);

        return () => {
            window.removeEventListener('load', updateNetworkStatus);
            window.removeEventListener('online', updateNetworkStatus);
            window.removeEventListener('offline', updateNetworkStatus);
        };
    }, []);

    return {isOnline};
};

export const usePrevious = <T>(value: T): T | undefined => {
    const ref = useRef<T | undefined>(undefined);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};
